import { parseUrl, stringifyUrl } from 'query-string'
import { useCallback } from 'react'

import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts'
import { isVariantAttributes } from 'shared/experiments/utils/featureFlags'
import { hasPersonalVariantAttributes } from 'views/components/organisms/FilteredProductList/SearchProductListItem/helpers'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import { isSingleVariantAttributeSERP } from 'views/utils/productUtils'

/**
 * @param {object} params
 * @param {AdServerMetadata} params.adServerMetadata
 * @param {object} params.product
 * @param {object} params.queryParams
 * @param {object} params.variantState
 * @returns {(eventName: string) => string}
 */
export const useProductUrl = params => {
  const { adServerMetadataContent, product, queryParams, variantState } = params

  const { publicRuntimeConfig } = useShopAndRuntimeConfig()

  const { pageProperties } = publicRuntimeConfig

  const { experiments, pageType } = pageProperties

  const isSearchPage = pageType === PAGE_TYPE_SEARCH_RESULTS

  const variantAttributesEnabled = isVariantAttributes(experiments)

  const isItemWithMultipleVariantAttributes =
    variantAttributesEnabled &&
    !isSingleVariantAttributeSERP(product.variants) &&
    product.variants?.length > 1

  const { trackingUrls = {} } = adServerMetadataContent

  const clickUrl = trackingUrls?.clickUrl

  let { canonicalURL } = variantState

  if (clickUrl) {
    canonicalURL = clickUrl
  }

  return useCallback(
    eventName => {
      if (isSearchPage) {
        const personalVariantAttributesQueryParameter =
          isItemWithMultipleVariantAttributes &&
          hasPersonalVariantAttributes(product.variant_attributes)
            ? { master: 'true' }
            : {}

        const newQueryParams = {
          eventName,
          ...queryParams,
          ...personalVariantAttributesQueryParameter,
        }

        const parsedProductUrl = parseUrl(canonicalURL, { decode: false })
        const query = {
          ...newQueryParams,
          ...parsedProductUrl.query,
        }

        const { url } = parsedProductUrl

        return stringifyUrl(
          {
            url,
            query,
          },
          { encode: false }
        )
      }

      return product.matchingOfferId
        ? `${canonicalURL}?offerId=${product.matchingOfferId}`
        : `${canonicalURL}`
    },
    [
      canonicalURL,
      isItemWithMultipleVariantAttributes,
      isSearchPage,
      product.variant_attributes,
      queryParams,
      product.matchingOfferId,
    ]
  )
}
